/**
 * AnalyticsTab — the product dashboard (Admin → User Management → Analytics).
 *
 * One call, `adminAnalyticsOverview` (functions/analytics), returns KPIs, a
 * daily series from analytics_daily (yesterday + today recomputed live),
 * weekly retention cohorts, the signup funnel, feature usage, acquisition
 * sources and plan mix. Built to read honestly with zero users: every panel
 * says what it will show and since when data has been tracked.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { View, Text, ScrollView, ActivityIndicator, useWindowDimensions } from 'react-native';
import { getAnalyticsOverview } from '../services/launchService';
import { A, PLAN_COLOR, Stat, Panel, BarChart, HBars, RetentionGrid, Segments, Btn, EmptyState, money, relTime, shortDate } from './admin/adminUi';
import Appear from './motion/Appear';
import { uiVar } from './floodlight/uiStyleCss';
import { AV, ADM_DISPLAY } from './floodlight/vars/admin';

const METRICS = [
    { value: 'activeUsers', label: 'Active users', color: A.green },
    { value: 'signups', label: 'Signups', color: A.accent },
    { value: 'searches', label: 'Searches', color: A.blue },
    { value: 'visitors', label: 'Visitors', color: '#F0ABFC' },
    { value: 'revenue', label: 'Revenue', color: A.amber },
];

export default function AnalyticsTab() {
    const { width } = useWindowDimensions();
    const two = width >= 1100;
    const [days, setDays] = useState(30);
    const [metric, setMetric] = useState('activeUsers');
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showTable, setShowTable] = useState(false);

    const load = useCallback(async (d) => {
        setLoading(true); setError(null);
        try { setData(await getAnalyticsOverview(d)); } catch (e) { setError(e.message); } finally { setLoading(false); }
    }, []);
    useEffect(() => { load(days); }, [days, load]);

    const k = data?.kpis || {};
    const m = METRICS.find((x) => x.value === metric);
    const pct = (v) => (v == null ? '—' : `${v}%`);
    const empty = data && !k.trackedUsers && !k.searches;

    return (
        <ScrollView contentContainerStyle={{ padding: 16, gap: 16, paddingBottom: 60 }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
                <View>
                    <Text style={{ color: AV.ink, fontSize: 22, fontWeight: '800', ...ADM_DISPLAY }}>Analytics</Text>
                    <Text style={{ color: AV.faint, fontSize: 12, marginTop: 2 }}>
                        {data ? `${shortDate(`${data.range.start}T12:00:00Z`)} – ${shortDate(`${data.range.end}T12:00:00Z`)} · activity tracked since ${data.trackingSince ? shortDate(new Date(data.trackingSince).toISOString()) : 'this release'}` : 'Loading…'}
                    </Text>
                </View>
                <View style={{ flexDirection: 'row', gap: 8, alignItems: 'center' }}>
                    <Segments value={days} onChange={setDays} options={[{ value: 7, label: '7d' }, { value: 30, label: '30d' }, { value: 90, label: '90d' }]} />
                    <Btn small label={loading ? 'Loading…' : 'Refresh'} onPress={() => load(days)} disabled={loading} />
                </View>
            </View>

            {error && <EmptyState icon="!" title="Couldn't load analytics" body={`${error}. The analytics functions may not be deployed yet.`} />}
            {loading && !data && <View style={{ padding: 40, alignItems: 'center' }}><ActivityIndicator color={A.accent} /></View>}

            {data && (
                <>
                    {empty && (
                        <Appear>
                            <View style={{ padding: 14, borderRadius: 12, borderWidth: 1, borderColor: uiVar('adm-accent-wash-35', 'rgba(167,139,250,0.35)'), backgroundColor: uiVar('adm-accent-wash-7', 'rgba(167,139,250,0.07)') }}>
                                <Text style={{ color: AV.ink, fontWeight: '700' }}>Waiting for real traffic</Text>
                                <Text style={{ color: AV.soft, fontSize: 12, marginTop: 4, lineHeight: 18 }}>
                                    Tracking is live: page views, searches, sign-ups, onboarding, bet-slip adds, checkouts and plan changes are recorded from this release on. Charts fill in as people use the app; daily totals are rolled up at 00:30 UTC and today is computed live.
                                </Text>
                            </View>
                        </Appear>
                    )}

                    <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 10 }}>
                        <Stat index={0} label="Active today" value={k.dau} hint={`${k.wau ?? 0} this week · ${k.mau ?? 0} this month`} tone={k.dau ? 'good' : undefined} />
                        <Stat index={1} label="Accounts" value={k.totalUsers} hint={`${k.signups ?? 0} new in range`} />
                        <Stat index={2} label="MRR" value={k.mrr} format={(v) => money(Math.round(v))} hint={`${k.paying ?? 0} paying (live)${k.mrrEstimated ? ` · ${k.mrrEstimated} at list price` : ''}`} tone={k.mrr ? 'good' : undefined} />
                        <Stat index={3} label="Revenue in range" value={k.revenue} format={(v) => money(Math.round(v))} hint={k.refunds || k.disputes ? `paid invoices · ${money(Math.round(k.refunds || 0))} refunded · ${k.disputes || 0} disputes` : 'paid invoices'} />
                        <Stat index={4} label="Searches" value={k.searches} hint={`success ${pct(k.searchSuccessRate)} · zero results ${pct(k.zeroResultRate)}`} tone={k.zeroResultRate > 20 ? 'warn' : undefined} />
                    </View>

                    <Panel
                        title="Daily trend"
                        subtitle={m?.value === 'visitors' ? 'Unique anonymous visitors (first-party id)' : m?.value === 'revenue' ? 'Paid invoices (live mode)' : undefined}
                        right={<Btn small label={showTable ? 'Chart' : 'Table'} onPress={() => setShowTable((t) => !t)} accessibilityLabel="Toggle table view" />}
                    >
                        <Segments value={metric} onChange={setMetric} options={METRICS.map(({ value, label }) => ({ value, label }))} />
                        {showTable ? (
                            <View style={{ gap: 2 }}>
                                {data.series.slice().reverse().map((r) => (
                                    <View key={r.day} style={{ flexDirection: 'row', gap: 12 }}>
                                        <Text style={{ color: AV.faint, fontSize: 12, width: 90, fontFamily: A.mono }}>{r.day}</Text>
                                        {METRICS.map((x) => <Text key={x.value} style={{ color: AV.soft, fontSize: 12, width: 92 }}>{r.missing ? '—' : x.value === 'revenue' ? money(r[x.value] || 0) : (r[x.value] ?? 0)}</Text>)}
                                    </View>
                                ))}
                            </View>
                        ) : (
                            <BarChart data={data.series} valueKey={metric} color={m?.color} label={`${m?.label} per day`} format={metric === 'revenue' ? (v) => money(v) : undefined} />
                        )}
                    </Panel>

                    <View style={{ flexDirection: two ? 'row' : 'column', gap: 16 }}>
                        <Panel title="Signup funnel" subtitle="People who signed up in this range, and how far they got" style={{ flex: 1 }}>
                            <HBars rows={data.funnel} color={A.accent} showPct total={data.funnel[1]?.count || 1} />
                        </Panel>
                        <Panel title="Retention by signup week" subtitle="% of each cohort active in later weeks" style={{ flex: 1 }}>
                            <RetentionGrid cohorts={data.retention} />
                        </Panel>
                    </View>

                    <View style={{ flexDirection: two ? 'row' : 'column', gap: 16 }}>
                        <Panel title="Feature usage" subtitle="All-time tracked events" style={{ flex: 1 }}>
                            <HBars rows={data.features.map((f) => ({ name: f.name.replace(/_/g, ' '), count: f.count }))} color={A.blue} />
                        </Panel>
                        <Panel title="Where users come from" subtitle="First touch: utm_source, else referrer" style={{ flex: 1 }}>
                            <HBars rows={data.sources} color="#F0ABFC" />
                        </Panel>
                        <Panel title="Plan mix" subtitle="Effective plan (lapsed plans count as Free)" style={{ flex: 1 }}>
                            <View style={{ gap: 8 }}>
                                {['FREE', 'BASIC', 'PRO', 'ULTIMATE'].map((p) => (
                                    <View key={p} style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                                        <View style={{ width: 10, height: 10, borderRadius: 3, backgroundColor: PLAN_COLOR[p] }} />
                                        <Text style={{ color: AV.soft, fontSize: 12, flex: 1 }}>{p.charAt(0) + p.slice(1).toLowerCase()}</Text>
                                        <Text style={{ color: AV.ink, fontSize: 12, fontVariant: ['tabular-nums'] }}>{data.planMix[p] || 0}</Text>
                                    </View>
                                ))}
                                <Text style={{ color: AV.faint, fontSize: 11 }}>{k.paying ?? 0} pay through Stripe; the rest are free or comped.</Text>
                            </View>
                        </Panel>
                    </View>
                    <Text style={{ color: AV.faint, fontSize: 11 }}>Generated {relTime(new Date().toISOString())}. Search totals come from the server search log (query_analytics); per-user search counts start with this release.</Text>
                </>
            )}
        </ScrollView>
    );
}
